"use client";
import { useMemo, type ReactNode } from "react";
import { ApiError } from "@/lib/api";
import { getWorkspaceSession } from "@/lib/api/administration";
import {
  useReadyWorkspaceContext,
  useWorkspaceContext,
} from "@/components/workspace/workspace-context";
import { useSupplierPortal } from "@/components/supplier-portal/supplier-portal-context";
import { EventRuntimeProvider } from "./event-runtime";
import { EventOperationNotices } from "./event-operation-notices";
import { BidRuntimeProvider } from "@/components/bidding/bid-runtime";
import { BidOperationNotices } from "@/components/bidding/bid-operation-notices";
import { EvaluationRuntimeProvider } from "@/components/evaluation/evaluation-runtime";
import { EvaluationOperationNotices } from "@/components/evaluation/evaluation-operation-notices";
import { AwardRuntimeProvider } from "@/components/awards/award-runtime";
import { AwardOperationNotices } from "@/components/awards/award-operation-notices";
import { TemplateRuntimeProvider } from "@/components/auctions/template-runtime";
import { TemplateNotices } from "@/components/auctions/template-notices";
import { AuctionRuntimeProvider } from "@/components/auctions/auction-runtime";
import { AuctionNotices } from "@/components/auctions/auction-notices";

export function WorkspaceEvents({ children }: { children: ReactNode }) {
  const context = useReadyWorkspaceContext();
  const workspace = useWorkspaceContext();
  const binding = useMemo(
    () => ({
      audience: "workspace" as const,
      tenantId: context.tenantId,
      accountId: context.accountId,
    }),
    [context.tenantId, context.accountId],
  );
  async function verify(signal: AbortSignal) {
    const epoch = workspace.getAuthorityEpoch();
    const session = (await getWorkspaceSession({ signal })).data;
    if (!workspace.isAuthorityCurrent(epoch) || signal.aborted) return false;
    if (
      session.tenantId !== binding.tenantId ||
      session.accountId !== binding.accountId
    ) {
      const error = new ApiError("Workspace binding changed", {
        kind: "unauthorized",
      });
      workspace.refreshAuthority(error);
      return false;
    }
    return true;
  }
  return (
    <EventRuntimeProvider
      key={`${binding.tenantId}:${binding.accountId}`}
      config={{
        binding,
        csrfToken: context.csrfRequestToken,
        verify,
        invalidate: workspace.refreshAuthority,
      }}
    >
      <BidRuntimeProvider
        config={{
          binding,
          csrfToken: context.csrfRequestToken,
          verify,
          invalidate: workspace.refreshAuthority,
        }}
      >
        <EvaluationRuntimeProvider
          config={{
            binding,
            csrfToken: context.csrfRequestToken,
            verify,
            invalidate: workspace.refreshAuthority,
          }}
        >
          <AwardRuntimeProvider
            config={{
              binding,
              csrfToken: context.csrfRequestToken,
              verify,
              invalidate: workspace.refreshAuthority,
            }}
          >
            <TemplateRuntimeProvider
              config={{
                binding,
                csrfToken: context.csrfRequestToken,
                verify,
                invalidate: workspace.refreshAuthority,
              }}
            >
              <AuctionRuntimeProvider
                config={{
                  binding,
                  csrfToken: context.csrfRequestToken,
                  verify,
                  invalidate: workspace.refreshAuthority,
                }}
              >
                <AuctionNotices />
                <TemplateNotices />
                <EventOperationNotices />
                <BidOperationNotices />
                <EvaluationOperationNotices />
                <AwardOperationNotices />
                {children}
              </AuctionRuntimeProvider>
            </TemplateRuntimeProvider>
          </AwardRuntimeProvider>
        </EvaluationRuntimeProvider>
      </BidRuntimeProvider>
    </EventRuntimeProvider>
  );
}
export function SupplierEvents({ children }: { children: ReactNode }) {
  const portal = useSupplierPortal();
  const context = portal.context!;
  const binding = useMemo(
    () => ({
      audience: "supplier" as const,
      tenantId: context.tenantId,
      accountId: context.accountId,
      supplierId: context.supplierId,
      accessId: context.accessId,
    }),
    [context.tenantId, context.accountId, context.supplierId, context.accessId],
  );
  return (
    <EventRuntimeProvider
      key={`${binding.tenantId}:${binding.accountId}:${binding.supplierId}:${binding.accessId}`}
      config={{
        binding,
        csrfToken: context.csrfRequestToken,
        verify: portal.ensureBinding,
        invalidate: portal.invalidate,
      }}
    >
      <BidRuntimeProvider
        config={{
          binding,
          csrfToken: context.csrfRequestToken,
          verify: portal.ensureBinding,
          invalidate: portal.invalidate,
        }}
      >
        <EventOperationNotices />
        <BidOperationNotices />
        {children}
      </BidRuntimeProvider>
    </EventRuntimeProvider>
  );
}
